// What a level is, once Tiled is out of the picture.
//
// `src/engine/campaign/` reads `.tmj` files and produces one of these. Nothing here
// has heard of Tiled, of pixels, or of a file — a `LevelSetup` is a plain
// value that travels in the opening command, which is what makes a run replay
// like any other game.
//
// The setup carries **every** region, hidden ones included. That is deliberate:
// `reveal-region` then names a region rather than carrying an army, so the
// reducer opens exactly what the level said and a malformed command cannot
// conjure anything. The cost is that hidden regions sit in local state where a
// curious player could read them, which for a single-player game is not a cost.

import { RULES } from "../rules";
import type { DepositRow } from "../industry/state";
import type { ZoneRow } from "../zones";
import {
  type Direction,
  fileOf,
  KING,
  type Piece,
  pieceColor,
  pieceType,
  rankOf,
  type Side
} from "../board";

/** A patch of ground and everything standing on it. */
export interface RegionSetup {
  id: string;
  /** Objective that opens it. Empty means it is on the board from the start. */
  revealedBy: string;
  /** Run a Gambit as it opens, as a stage boundary already does. */
  gambit: boolean;
  cells: number[];
  pieces: [square: number, piece: Piece][];
  belts: [square: number, direction: Direction, owner: Side][];
  heaps: [square: number, amount: number][];
  /**
   * Refined material lying on the ground, as `[square, grade, count]`.
   *
   * **A heap is a count and cannot carry a grade** — scrap is grade-0 by
   * definition, being what a corpse leaves — so a level that wants to start
   * the player with silver has nowhere to put it. This is `industry.dropped`,
   * the ground: anyone who walks over it picks it up, and a king's sweep lifts
   * it exactly as it lifts scrap, which means a `collect` objective counts it
   * without being taught anything.
   */
  metal?: [square: number, grade: number, count: number][];
  /** Coal lying loose, as `[square, count]` (§31 M2) — what the room's metal is raised with. */
  coal?: [square: number, count: number][];
  /**
   * Ore in the ground (a Tiled `deposit`), as the command's `DepositRow`.
   * Authored where a zone's are rolled, so an opener can promise a pawn
   * something to dig on its first step.
   */
  deposits?: DepositRow[];
  /**
   * Zones the room grows (§31 D3, a Tiled `zone` object): rectangles whose
   * ground and contents come off the seed as the room opens (`growRegionZones`).
   */
  zones?: ZoneRow[];
  promotion: [square: number, uses: number][];
  /** Overrides a pawn's default forward facing. */
  facing: [square: number, direction: Direction][];
  /**
   * Buildings this room stands on the board, as `[square, kind, owner]`.
   *
   * **A level could place every other thing in the game and not a building.**
   * A store is a machine and a turret is a gun a run has to earn, and both are
   * exactly what an authored camp wants: a warehouse says an army has been
   * here a while, an emplacement says this ground is defended. Without them a
   * room could describe a factory and never a fortification.
   *
   * A structure's square is deliberately **not** a cell — `openRegion` filters
   * it out of the ground it lays — so one placed here takes its square off the
   * board in the same breath as the region adds it. That is the same rule
   * `build` follows and the reason "no piece may ever occupy one" stays true.
   */
  structures?: [square: number, kind: number, owner: Side][];
  /**
   * What this room is worth as a **camp**, when it is used as one.
   *
   * Optional, and absent on every room that is only ever part of its own
   * level. `ai/formations.ts` gates a procedural camp on exactly these three
   * — a citadel against a lone king is a dead end and a three-pawn outpost
   * against a full army is a formality — and an authored camp needs the same
   * gates or it is a wall with a stage counter behind it.
   */
  camp?: { weight: number; minStage?: number; minPlayerPieces?: number; maxPlayerPieces?: number };
  /**
   * What kind of army a side in this room fields, as `[side, kingKind]`.
   *
   * **A room can hand a camp its faction, not just its pieces** — only the
   * factory army today (§31 R2); the field is the faction hook.
   *
   * Per side rather than per room, because a room may hold two camps and they
   * need not be the same thing — and because `kingKind` is indexed by side
   * everywhere else, so anything else would be a second way of saying it.
   */
  kings?: [side: Side, kind: number][];
  /**
   * The square of *existing* ground this region joins on at.
   *
   * **Authored, not inferred.** A region that opens across a gap reads as the
   * board having broken (§17), and the loader used to have no way to know
   * whether one bridged: it saw a bag of cells with no statement about how they
   * meet what is already there. Working it out from geometry is the wrong fix —
   * "nearest existing cell" is a guess that is right until a level wants two
   * lobes or a deliberate isthmus.
   *
   * A `connect` point object in the region's group says it outright. Optional,
   * because the opening region has nothing to connect *to*; when it is there it
   * is checked, and it is what the camera leads to when the ground opens.
   */
  connect?: number;
}

/**
 * `collect` is about ore *picked up*, not ore *held* — and that difference is
 * the whole reason it exists. A `deliver` counts what is standing on a square
 * right now, so an economy that is working spends its way back under the
 * target: `first-light`'s opening asked for twelve in the king's hands and a
 * machine playing it peaked at ten of twelve across every seed, because it
 * refined and forged the ore it had just walked over. Collecting is a thing
 * that happened; holding is a thing that can stop being true.
 */
export type ObjectiveKind = "defeat" | "deliver" | "reach" | "collect";

export interface ObjectiveSetup {
  id: string;
  /**
   * Run a Productivity Gambit as this objective's ground opens.
   *
   * On the objective rather than on the room, because ground opening *is* an
   * objective completing — and a room is a group layer, which is the one thing
   * in a Tiled file an editor is least likely to offer a properties panel for.
   */
  gambit?: boolean;
  /** Sequence position. A later objective is not evaluated until earlier ones
   *  are done, which is what makes a level a sequence rather than a checklist. */
  order: number;
  kind: ObjectiveKind;
  /** `defeat`: the army to run out of moves. */
  side?: Side;
  /** `deliver` and `collect`: how much, and of at least what grade. */
  count?: number;
  grade?: number;
  /** `reach`: the square to stand on. */
  square?: number;
  /**
   * `deliver`: deliver to `square` rather than to your king.
   *
   * **An explicit opt-in, because `square` is set on every objective.** The
   * loader takes it from the Tiled object's own position — every objective is
   * a marker and every marker has one — so its *presence* says nothing about
   * intent. Reading it as "deliver here" made `dig-in` measure the square its
   * label happened to sit on, which holds nothing, and the opening of every
   * run became uncompletable: the king filled up and the camp never arrived.
   */
  deliverTo?: boolean;
  /** Regions this opens when it completes. */
  reveals: string[];
  /** One line, shown to the player. */
  text: string;
}

/**
 * Per-scenario constant overrides, read off a `knobs` object.
 *
 * **Every key is a numeric key of `RULES`, checked at load.** A knob naming
 * something that does not exist is a fault rather than a silent no-op — the
 * whole failure mode of a settings bag is that a typo looks exactly like a
 * setting that did not matter, and a level author has no way to tell the two
 * apart from inside Tiled.
 *
 * Read through `levelKnob`, never by reaching into the record: the fallback to
 * `RULES` has to be in one place or half the readers will treat "absent" as
 * zero.
 */
export type LevelKnobs = Record<string, number>;

/** Numeric keys of `RULES`, which is what a knob is allowed to name. */
export const KNOB_KEYS: readonly string[] = Object.entries(RULES)
  .filter(([, value]) => typeof value === "number")
  .map(([key]) => key)
  .sort();

/**
 * What this scenario says a constant is, or what the game says.
 *
 * The single reader. A knob only *does* anything once something asks through
 * here — which is deliberate: a scenario cannot reach into the rules from a
 * distance, it can only answer a question the code already asks.
 */
export function levelKnob(level: LevelSetup | undefined, key: string): number {
  const override = level?.knobs?.[key];
  if (typeof override === "number") return override;
  return (RULES as unknown as Record<string, number>)[key];
}

export interface LevelSetup {
  levelId: string;
  levelName: string;
  playerSide: Side;
  regions: RegionSetup[];
  objectives: ObjectiveSetup[];
  /** Constants this scenario overrides. See `LevelKnobs`. */
  knobs?: LevelKnobs;
  /**
   * This scenario is the end of the run: finishing it wins (PLAN §12.4.9).
   *
   * **Declared, never inferred.** Every level's objectives eventually complete
   * — that is what an objective is — and waves are what happens *after* an
   * authored scenario runs out, so "nothing left to do" cannot mean "the run is
   * over" on its own. Without this, clearing the opening tutorial would end the
   * run five minutes in.
   *
   * Defaults false, which is the safe reading: a scenario that forgets to say
   * so hands the player to the wave machine, and a run that goes on too long is
   * a far smaller failure than one that stops after the tutorial.
   */
  final?: boolean;
}

export interface ObjectiveState {
  id: string;
  done: boolean;
  /** How far along, for the ones that count. Latched: it never goes down. */
  progress: number;
}

/**
 * Why a level will not load.
 *
 * The loader cannot prove a level is solvable, but it can catch the cheap
 * cases — and a level that soft-locks a run twenty minutes in is worse than
 * one that refuses to start (PLAN §12.7, hazard 10).
 */
export type LevelFault =
  | { fault: "duplicate-objective"; id: string }
  | { fault: "duplicate-region"; id: string }
  | { fault: "unknown-region"; id: string; from: string }
  | { fault: "unknown-objective"; id: string; from: string }
  | { fault: "objective-order-gap"; order: number }
  | { fault: "defeat-without-king"; id: string; side: Side }
  | { fault: "deliver-beyond-supply"; id: string; want: number; have: number }
  | { fault: "deliver-beyond-hands"; id: string; want: number; hold: number }
  | { fault: "region-overlaps"; id: string; square: number }
  | { fault: "objective-adrift"; id: string; square: number }
  | { fault: "connect-adrift"; id: string; square: number }
  | { fault: "connect-inside"; id: string; square: number }
  | { fault: "piece-in-a-hole"; id: string; square: number }
  | { fault: "army-without-pieces"; id: string; side: number }
  | { fault: "marker-in-a-hole"; id: string; square: number }
  | { fault: "structure-occupied"; id: string; square: number }
  | { fault: "no-start-region" };

/**
 * Everything wrong with a level, rather than the first thing.
 *
 * All of it at once, because an author fixing a map one error at a time is an
 * author reloading Tiled six times.
 */
export function validateLevel(level: LevelSetup): LevelFault[] {
  const faults: LevelFault[] = [];

  const regions = new Set<string>();
  for (const region of level.regions) {
    if (regions.has(region.id)) faults.push({ fault: "duplicate-region", id: region.id });
    regions.add(region.id);
  }

  const objectives = new Set<string>();
  for (const objective of level.objectives) {
    if (objectives.has(objective.id)) {
      faults.push({ fault: "duplicate-objective", id: objective.id });
    }
    objectives.add(objective.id);
  }

  if (!level.regions.some((region) => region.revealedBy === "")) {
    faults.push({ fault: "no-start-region" });
  }

  for (const region of level.regions) {
    if (region.revealedBy !== "" && !objectives.has(region.revealedBy)) {
      faults.push({ fault: "unknown-objective", id: region.revealedBy, from: region.id });
    }
  }

  for (const objective of level.objectives) {
    for (const id of objective.reveals) {
      if (!regions.has(id)) {
        faults.push({ fault: "unknown-region", id, from: objective.id });
      }
    }
    // A `defeat` naming a side with no king on the whole level is an objective
    // that can never complete.
    if (objective.kind === "defeat") {
      // Through the accessors, never the bit layout: a hand-rolled `piece & 7`
      // here would keep compiling and quietly stop meaning "king" the day the
      // packing changes.
      const hasKing = level.regions.some((region) =>
        region.pieces.some(
          ([, piece]) => pieceColor(piece) === objective.side && pieceType(piece) === KING
        )
      );
      if (!hasKing) {
        faults.push({
          fault: "defeat-without-king",
          id: objective.id,
          side: objective.side ?? -1
        });
      }
    }

    // A `deliver` asking for more than the level puts on the floor is an
    // objective nobody can finish — and it is silent, because the run plays
    // perfectly well right up until the moment it cannot continue. This is the
    // check PLAN §17 wants before the opening is gated on one: the gate is
    // only safe if the loader can prove it opens.
    //
    // Everything seeded, wherever it is: a later region's heaps count, since
    // the objective may well be finished after it opens.
    if (objective.kind === "deliver") {
      const want = objective.count ?? 1;
      // Seeded metal counts as supply too. It is already refined, so it can
      // only ever *help* a grade-0 delivery — but leaving it out would make a
      // level that opens the player on silver look short of its own target.
      const have = level.regions.reduce(
        (total, region) =>
          total +
          region.heaps.reduce((sum, [, amount]) => sum + amount, 0) +
          (region.metal ?? []).reduce((sum, [, , count]) => sum + count, 0),
        0
      );
      // Only for grade 0. Above it the material has been *refined*, and how
      // much a chain can produce from a given pile is not a thing a loader can
      // answer — cooking is quantity-neutral, but a unit may be spent on a
      // recipe long before it reaches a king.
      if ((objective.grade ?? 0) === 0 && want > have) {
        faults.push({ fault: "deliver-beyond-supply", id: objective.id, want, have });
      }
      // And a delivery *to your king* is measured in the king's hands at once,
      // so asking for more than a king can hold is unachievable however much
      // is on the floor.
      //
      // Only to your king. A delivery to a **named square** — 7g's rocket pad
      // — has no bound this loader can prove: that square may end up a
      // warehouse, a piece or bare ground, the player decides which during
      // play, and material on the ground is not capped at all. Keeping the
      // king's limit would refuse the finale outright, since `rocketCost` is
      // meant to be a project and is far past anything a king could carry —
      // and inventing a bound from `storeCapacity` would refuse a valid level
      // whose pad is simply the floor. A missed fault costs a level that
      // cannot finish; a false one costs a level that cannot start, and this
      // check exists to catch the cheap cases rather than to be complete.
      if (!objective.deliverTo && want > RULES.kingBuffer) {
        faults.push({
          fault: "deliver-beyond-hands",
          id: objective.id,
          want,
          hold: RULES.kingBuffer
        });
      }
    }
  }

  // **An army declaration about a side the room does not place is a typo.**
  //
  // The same failure `knobs` faults on and for the same reason: from inside
  // Tiled there is no telling a misspelled `side` from a setting that did not
  // matter. Both read as "nothing happened", and the thing that did not happen
  // is a camp arriving as the army it was written to be — which is invisible
  // until somebody plays the room and it fights like an ordinary factory.
  for (const region of level.regions) {
    for (const [side] of region.kings ?? []) {
      const has = region.pieces.some(([, piece]) => pieceColor(piece) === side);
      if (!has) faults.push({ fault: "army-without-pieces", id: region.id, side });
    }
  }

  // Orders must run 1..n with no gap, or "the next objective" is ambiguous.
  const orders = [...level.objectives].map((o) => o.order).sort((a, b) => a - b);
  orders.forEach((order, index) => {
    if (order !== index + 1) faults.push({ fault: "objective-order-gap", order });
  });

  // A piece must stand on ground its *own* region provides.
  //
  // This is the lesson `ai/formations.ts` already paid for: a formation whose
  // pieces land in a hole is a piece that cannot move and a king that cannot
  // be mated. On an authored level it is worse, because the hole can be
  // another region's ground — the piece is silently dropped when the level
  // opens and quietly appears later, or never. The whole enemy camp of the
  // first level went missing this way, and the symptom was a stalemate on turn
  // one.
  for (const region of level.regions) {
    const ground = new Set(region.cells);
    for (const [square] of region.pieces) {
      if (!ground.has(square)) faults.push({ fault: "piece-in-a-hole", id: region.id, square });
    }
    for (const [square] of [
      ...region.heaps,
      ...(region.metal ?? []),
      ...(region.coal ?? []),
      ...(region.deposits ?? []),
      ...region.promotion,
      ...region.facing,
      // A building stands on the region's own floor and then takes that square
      // *off* the board, so it is authored on the ground like everything else
      // and adrift for the same reason if it is not.
      ...(region.structures ?? [])
    ]) {
      if (!ground.has(square)) faults.push({ fault: "marker-in-a-hole", id: region.id, square });
    }

    // **A building's square stops being a cell, so anything else authored on it
    // is unreachable the moment the level opens** — a heap nobody can mine, a
    // belt with no ground under it, a promotion square no pawn can be sent to.
    // That is the orphan class `orphans.test.ts` guards at runtime, arriving
    // here from the one direction it cannot see: written into the map by hand.
    const built = new Set((region.structures ?? []).map(([square]) => square));
    if (built.size > 0) {
      for (const [square] of [
        ...region.pieces,
        ...region.heaps,
        ...(region.metal ?? []),
        ...(region.coal ?? []),
        ...(region.deposits ?? []),
        ...region.promotion,
        ...region.belts
      ]) {
        if (built.has(square)) faults.push({ fault: "structure-occupied", id: region.id, square });
      }
      // And two buildings on one square is one building and a lost one.
      const seen = new Set<number>();
      for (const [square] of region.structures ?? []) {
        if (seen.has(square)) faults.push({ fault: "structure-occupied", id: region.id, square });
        seen.add(square);
      }
    }
  }

  // Two regions claiming a square is a region that would pave over another's
  // ground — the authored equivalent of a wave landing on your factory.
  const claimed = new Map<number, string>();
  for (const region of level.regions) {
    for (const square of region.cells) {
      const owner = claimed.get(square);
      if (owner !== undefined && owner !== region.id) {
        faults.push({ fault: "region-overlaps", id: region.id, square });
      }
      claimed.set(square, region.id);
    }
  }

  // An objective's marker has to stand on ground the level creates.
  //
  // **Found by an author, not by a test.** `dig-in` sat at the top-left corner
  // of the Tiled canvas — a square no region ever paves — and nothing noticed,
  // because a `deliver` to your king never reads its own square. The symptom
  // was a marker stranded in the corner of the map in the editor, which is
  // exactly the kind of thing that reads as the *tool* being wrong.
  //
  // Every objective, not only the ones whose square is load-bearing: a marker
  // is how an author finds the objective on a four-hundred-square board, and
  // one parked off the map is a marker that has stopped doing its job.
  const paved = new Set<number>();
  for (const region of level.regions) {
    for (const square of region.cells) paved.add(square);
  }
  for (const objective of level.objectives) {
    if (objective.square === undefined) continue;
    if (!paved.has(objective.square)) {
      faults.push({ fault: "objective-adrift", id: objective.id, square: objective.square });
    }
  }

  // Where a hidden region joins the board, checked against the board it will
  // actually join. **A region that opens across a gap reads as the board
  // breaking**, and this is the one thing about a reveal an author cannot see
  // in Tiled — the region is drawn beside its neighbours on one canvas, so it
  // always *looks* attached.
  //
  // Two ways to get it wrong, and they are different mistakes:
  for (const region of level.regions) {
    if (region.connect === undefined) continue;
    const own = new Set(region.cells);
    // Pointing at its own ground, which says nothing: every region touches
    // itself. The marker is for the square on the *other* side of the join.
    if (own.has(region.connect)) {
      faults.push({ fault: "connect-inside", id: region.id, square: region.connect });
      continue;
    }
    // Pointing at nothing this level ever creates — a typo, or a marker left
    // behind when the ground around it was redrawn.
    if (!paved.has(region.connect)) {
      faults.push({ fault: "connect-adrift", id: region.id, square: region.connect });
      continue;
    }
    // Attached, or it is not a bridge. Chebyshev, because that is what
    // "touching" means everywhere else on this board.
    const touching = region.cells.some(
      (square) =>
        Math.max(
          Math.abs(fileOf(square) - fileOf(region.connect!)),
          Math.abs(rankOf(square) - rankOf(region.connect!))
        ) <= 1
    );
    if (!touching) {
      faults.push({ fault: "connect-adrift", id: region.id, square: region.connect });
    }
  }

  return faults;
}

/** A fault, in a sentence an author can act on. */
export function faultMessage(fault: LevelFault): string {
  switch (fault.fault) {
    case "duplicate-objective":
      return `two objectives are called "${fault.id}"`;
    case "duplicate-region":
      return `two rooms are numbered "${fault.id}"`;
    case "unknown-region":
      return `objective "${fault.from}" reveals "${fault.id}", which is not a room in this level`;
    case "unknown-objective":
      return `room "${fault.from}" is revealed by "${fault.id}", which is not an objective`;
    case "objective-order-gap":
      return `objective orders must run 1, 2, 3… with no gaps; found ${fault.order}`;
    case "deliver-beyond-supply":
      return `objective "${fault.id}" wants ${fault.want} units delivered and the level seeds ${fault.have}`;
    case "deliver-beyond-hands":
      return `objective "${fault.id}" wants ${fault.want} units in a king that holds ${fault.hold}`;
    case "defeat-without-king":
      return `objective "${fault.id}" asks you to beat side ${fault.side}, which has no king anywhere in the level`;
    case "region-overlaps":
      return `room "${fault.id}" claims a square another room already has`;
    case "army-without-pieces":
      return `room ${fault.id} names an army for side ${fault.side}, which it places no pieces for`;
    case "piece-in-a-hole":
      return `room "${fault.id}" puts a piece on a square it does not pave`;
    case "marker-in-a-hole":
      return `room "${fault.id}" puts a marker on ground it does not pave`;
    case "structure-occupied":
      return (
        `room "${fault.id}" puts a building on square ${fault.square} and something ` +
        `else on it too — a building takes its square off the board, so whatever ` +
        `shares it can never be reached`
      );
    case "objective-adrift":
      return (
        `objective "${fault.id}" is marked at ${fault.square}, which no region ` +
        `paves — put the marker on the square the objective is about`
      );
    case "connect-adrift":
      return (
        `region "${fault.id}" connects from ${fault.square}, which is not ground ` +
        `it touches — the marker goes on the square the region joins on at`
      );
    case "connect-inside":
      return (
        `region "${fault.id}" connects from ${fault.square}, which is its own ` +
        `ground — the marker goes on the far side of the join`
      );
    case "no-start-region":
      return "no room starts on the board — one must be revealed by no objective";
  }
}
